import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { ProductGrid } from "@/components/product/ProductGrid";
import { SortSelect } from "@/components/shop/SortSelect";
import { QuickViewModal } from "@/components/product/QuickViewModal";
import { useCollection } from "@/hooks/useCatalog";

export default function CollectionPage() {
  const { handle } = useParams();
  const [sort, setSort] = useState("featured");
  const [limit, setLimit] = useState(24);
  const [material, setMaterial] = useState("all");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [quick, setQuick] = useState(null);

  const { data, isLoading, isFetching } = useCollection(handle, { sort, limit });
  const collection = data?.collection;
  const allItems = useMemo(() => data?.items || [], [data]);

  const materials = useMemo(() => Array.from(new Set(allItems.map((p) => p.material).filter(Boolean))), [allItems]);
  const items = useMemo(
    () =>
      allItems.filter((p) => (material === "all" || p.material === material) && (!inStockOnly || p.available)),
    [allItems, material, inStockOnly]
  );

  const jsonLd = collection && {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: collection.title,
    description: collection.description,
  };

  return (
    <div>
      <SEO title={collection?.title || "Collection"} description={collection?.description} image={collection?.banner} jsonLd={jsonLd} />

      {/* Banner */}
      <section className="relative h-[42vh] min-h-[300px] w-full overflow-hidden">
        {collection?.banner && (
          <img src={collection.banner} alt={collection.title} className="absolute inset-0 h-full w-full object-cover object-center" />
        )}
        <div className="absolute inset-0 bg-ink/45" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col items-center justify-center px-4 text-center text-ivory">
          <h1 className="animate-fade-up font-serif text-4xl font-light sm:text-5xl lg:text-6xl">{collection?.title || "Collection"}</h1>
          {collection?.description && (
            <p className="mt-4 max-w-2xl animate-fade-up text-sm leading-relaxed text-ivory/85 sm:text-base reveal-delay-1">
              {collection.description}
            </p>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Collections", to: "/collections" }, { label: collection?.title || handle }]} />

        {/* Toolbar */}
        <div className="mt-6 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setMaterial("all")}
              className={`border px-4 py-2 text-xs uppercase tracking-wider transition-colors ${material === "all" ? "border-ink bg-ink text-ivory" : "border-border text-ink hover:border-gold"}`}
            >
              All
            </button>
            {materials.map((m) => (
              <button
                key={m}
                onClick={() => setMaterial(m)}
                className={`border px-4 py-2 text-xs uppercase tracking-wider transition-colors ${material === m ? "border-ink bg-ink text-ivory" : "border-border text-ink hover:border-gold"}`}
              >
                {m}
              </button>
            ))}
            <label className="ml-2 flex cursor-pointer items-center gap-2 text-xs uppercase tracking-wider text-ink">
              <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} className="accent-[#C5A059]" />
              In stock
            </label>
          </div>
          <SortSelect value={sort} onChange={setSort} />
        </div>

        <p className="mb-6 mt-6 text-xs uppercase tracking-wider text-muted-foreground">{items.length} Pieces</p>

        <ProductGrid products={items} loading={isLoading} onQuickView={setQuick} />

        {data?.has_more && (
          <div className="mt-14 text-center">
            <button
              onClick={() => setLimit((l) => l + 12)}
              disabled={isFetching}
              className="border border-ink px-10 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-ivory disabled:opacity-60"
            >
              {isFetching ? "Loading…" : "Load More"}
            </button>
          </div>
        )}
      </div>

      <QuickViewModal product={quick} open={!!quick} onOpenChange={(o) => !o && setQuick(null)} />
    </div>
  );
}
